import {
  heightAccessor,
  widthAccessor,
  xAccessor,
  yAccessor,
} from './bar-accessors.js';
import { BarCanvas } from '../../../core/components/canvas/components/BarCanvas.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { POINT_OVERLAY_PADDING } from '../../../core/components/data-point/constants.js';
import { DEFAULT_FILL_OPACITY } from '../../../core/constants.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useBarData } from '../../hooks/useBarData.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  BarMetadata,
  BarSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import { type BarDatapoint } from '../../types/xy-chart.js';
import { getCurrentScale, getDimensionScale } from '../../utils/get-scale.js';
import { relAccessor } from '../../utils/value-representation.js';

interface BarDisplayProps {
  axisIdToScales: AxisIdToScales;

  series: BarSeriesWithDatapoints;
  metadata: BarMetadata;
}

export function isXBaselineAccessor(datapoint?: BarDatapoint): boolean {
  return !!datapoint && datapoint.x0 !== undefined;
}

export function isYBaselineAccessor(datapoint?: BarDatapoint): boolean {
  return !!datapoint && datapoint.y0 !== undefined;
}

export function BarDisplay({
  axisIdToScales,
  series,
  metadata,
}: Readonly<BarDisplayProps>) {
  const { height, width } = useGridPlotSize();

  const xScale = getCurrentScale(
    axisIdToScales,
    series.xAxisId,
    isXBaselineAccessor(series.data[0]) ? series.type : undefined,
  );
  const dimensionXScale = getDimensionScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(
    axisIdToScales,
    series.yAxisId,
    isYBaselineAccessor(series.data[0]) ? series.type : undefined,
  );
  const dimensionYScale = getDimensionScale(axisIdToScales, series.yAxisId);
  const { color } = metadata;

  const { datapointMetadataMap, axisIdToScaleDomain } = useXYChartData();
  const { seriesToMetadata } = useBarData();

  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);

  return (
    <BarCanvas<BarDatapoint>
      key={series.id}
      data={series.data}
      canvasHeight={height}
      canvasWidth={width}
      opacity={() => DEFAULT_FILL_OPACITY}
      color={() => color.Default}
      x={xAccessor(
        xScale,
        datapointMetadataMap,
        dimensionXScale,
        series.id,
        relAccessor(xAxis, axisIdToScaleDomain),
      )}
      y={yAccessor(
        yScale,
        datapointMetadataMap,
        dimensionYScale,
        series.id,
        relAccessor(yAxis, axisIdToScaleDomain),
      )}
      width={widthAccessor(
        xScale,
        datapointMetadataMap,
        dimensionXScale,
        relAccessor(xAxis, axisIdToScaleDomain),
      )}
      height={heightAccessor(
        yScale,
        datapointMetadataMap,
        dimensionYScale,
        relAccessor(yAxis, axisIdToScaleDomain),
      )}
      enableDotOverflow
      overlayPadding={POINT_OVERLAY_PADDING}
    />
  );
}

BarDisplay['displayName'] = 'BarDisplay';
